import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

const FOCUSABLE_SELECTOR =
  "input, textarea, ion-input, ion-textarea, ion-searchbar, [contenteditable='true']";

const FIELD_WRAPPER_SELECTOR = [
  ".sheet-composer",
  ".input-footer",
  ".ds-otp-inputWrap",
  ".otp-boxes",
  "ion-item",
  ".form-group",
  ".field",
].join(", ");

const getVisualViewportKeyboardHeight = () => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;

  const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
  return Math.max(0, Math.round(overlap));
};

const getScrollElement = (target) => {
  if (!target || !target.closest) return target;
  return target.closest(FIELD_WRAPPER_SELECTOR) || target;
};

const isTextEntryTarget = (target) => {
  if (!target || !target.matches) return false;
  if (!target.matches(FOCUSABLE_SELECTOR)) return false;

  const type = String(target.getAttribute("type") || "").toLowerCase();
  return ![
    "button",
    "checkbox",
    "file",
    "hidden",
    "image",
    "radio",
    "range",
    "reset",
    "submit",
  ].includes(type);
};

const scrollFocusedFieldIntoView = () => {
  const active = document.activeElement;
  if (!isTextEntryTarget(active)) return;

  const target = getScrollElement(active);
  if (!target?.scrollIntoView) return;

  target.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest",
  });
};

const setKeyboardVars = (height) => {
  const resolvedHeight = Math.max(0, Math.round(Number(height) || 0));
  const root = document.documentElement;

  root.style.setProperty("--app-keyboard-height", `${resolvedHeight}px`);
  root.style.setProperty("--app-keyboard-visible", resolvedHeight > 0 ? "1" : "0");
  root.classList.toggle("app-keyboard-open", resolvedHeight > 0);
};

export default function KeyboardAvoidanceManager() {
  const visibleHeightRef = useRef(0);
  const scrollTimerRef = useRef(null);

  useEffect(() => {
    const scheduleFocusedScroll = (delay = 140) => {
      window.clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(scrollFocusedFieldIntoView, delay);
    };

    const setVisibleHeight = (height) => {
      const fallbackHeight = getVisualViewportKeyboardHeight();
      const nextHeight = Math.max(Number(height) || 0, fallbackHeight);
      visibleHeightRef.current = nextHeight;
      setKeyboardVars(nextHeight);
      if (nextHeight > 0) scheduleFocusedScroll(90);
    };

    const hideKeyboard = () => {
      visibleHeightRef.current = 0;
      setKeyboardVars(0);
    };

    const onFocusIn = (event) => {
      if (!isTextEntryTarget(event.target)) return;

      if (visibleHeightRef.current > 0 || getVisualViewportKeyboardHeight() > 0) {
        scheduleFocusedScroll(80);
        return;
      }

      scheduleFocusedScroll(Capacitor.isNativePlatform() ? 280 : 120);
    };

    const onVisualViewportChange = () => {
      const height = getVisualViewportKeyboardHeight();
      if (height > 0) {
        setVisibleHeight(height);
      } else if (visibleHeightRef.current > 0) {
        hideKeyboard();
      }
    };

    const subscriptions = [];
    let cancelled = false;

    document.addEventListener("focusin", onFocusIn, true);
    window.visualViewport?.addEventListener("resize", onVisualViewportChange);
    window.visualViewport?.addEventListener("scroll", onVisualViewportChange);

    if (Capacitor.isNativePlatform()) {
      (async () => {
        const onShow = (info) => setVisibleHeight(info?.keyboardHeight || 0);
        const onHide = () => hideKeyboard();

        let listeners = [];
        try {
          listeners = await Promise.all([
            Keyboard.addListener("keyboardWillShow", onShow),
            Keyboard.addListener("keyboardDidShow", onShow),
            Keyboard.addListener("keyboardWillHide", onHide),
            Keyboard.addListener("keyboardDidHide", onHide),
          ]);
        } catch {
          listeners.forEach((listener) => listener?.remove?.());
          return;
        }

        if (cancelled) {
          listeners.forEach((listener) => listener?.remove?.());
          return;
        }

        subscriptions.push(...listeners);
      })();
    }

    return () => {
      cancelled = true;
      window.clearTimeout(scrollTimerRef.current);
      subscriptions.forEach((listener) => listener?.remove?.());
      document.removeEventListener("focusin", onFocusIn, true);
      window.visualViewport?.removeEventListener("resize", onVisualViewportChange);
      window.visualViewport?.removeEventListener("scroll", onVisualViewportChange);
      setKeyboardVars(0);
    };
  }, []);

  return null;
}
